// /**
//  * htmlUtils.ts — Utility functions for the email body HTML pipeline.
//  *
//  * Used by TemplatesPage and SequencesPage to bridge between legacy Jinja2
//  * plain-text bodies and the new HTML-first storage from RichTextEditor.
//  *
//  * Jinja2 variables in this project use the `{{ var_name }}` format with
//  * spaces. These survive HTML storage fine — Tiptap stores them as text nodes
//  * inside <p> elements, and the backend's Jinja2 renderer substitutes them
//  * in the HTML string before sending.
//  */

// /**
//  * Ensures a string is valid HTML suitable for loading into the Tiptap editor.
//  *
//  * - Already HTML (starts with a tag): returned as-is.
//  * - Plain text (including Jinja2 template bodies with \n\n paragraphs):
//  *   each double-newline block becomes a <p>, single \n becomes <br>.
//  * - Empty input: returns empty string (Tiptap shows placeholder).
//  */
// export function ensureHtml(content: string | null | undefined): string {
//   if (!content) return "";
//   const trimmed = content.trim();
//   if (!trimmed) return "";

//   // Already HTML: starts with an opening tag and has at least one closing tag.
//   if (/^<[a-zA-Z]/.test(trimmed) && /<\/[a-zA-Z]/.test(trimmed)) {
//     return content;
//   }

//   // Plain text / Jinja2 template → HTML paragraphs.
//   return trimmed
//     .split(/\n{2,}/)
//     .filter(Boolean)
//     .map((block) => `<p>${block.trim().replace(/\n/g, "<br>")}</p>`)
//     .join("");
// }

// /**
//  * Strips all HTML tags and decodes common entities.
//  * Used for card preview text and word-count calculations.
//  */
// export function stripHtml(html: string | null | undefined): string {
//   if (!html) return "";
//   return html
//     .replace(/<br\s*\/?>/gi, " ")
//     .replace(/<\/p>/gi, " ")
//     .replace(/<[^>]+>/g, "")
//     .replace(/&amp;/g, "&")
//     .replace(/&lt;/g, "<")
//     .replace(/&gt;/g, ">")
//     .replace(/&nbsp;/g, " ")
//     .replace(/&quot;/g, '"')
//     .replace(/&#39;/g, "'")
//     .replace(/\s{2,}/g, " ")
//     .trim();
// }

// /**
//  * Returns true when the body string is HTML output from the Tiptap RTE,
//  * as opposed to legacy plain text.
//  *
//  * Used by the backend scheduler patch to route body correctly through
//  * body_html vs body_text MailBridge fields.
//  */
// export function isHtmlBody(body: string | null | undefined): boolean {
//   if (!body) return false;
//   const s = body.trimStart();
//   return (
//     s.startsWith("<") &&
//     (s.includes("</p>") ||
//       s.includes("</h") ||
//       s.includes("<br") ||
//       s.includes("</ul>") ||
//       s.includes("</ol>") ||
//       s.includes("</li>") ||
//       s.includes("</strong>") ||
//       s.includes("</em>"))
//   );
// }
/**
 * htmlUtils.ts — Utility functions for the email body HTML pipeline.
 *
 * Used by TemplatesPage and SequencesPage to bridge between legacy Jinja2
 * plain-text bodies and the new HTML-first storage from RichTextEditor.
 *
 * Jinja2 variables in this project use the `{{ var_name }}` format with
 * spaces. These survive HTML storage fine — Tiptap stores them as text nodes
 * inside <p> elements, and the backend's Jinja2 renderer substitutes them
 * in the HTML string before sending.
 */

/**
 * Ensures a string is valid HTML suitable for loading into the Tiptap editor.
 *
 * - Already HTML (starts with a tag): returned as-is.
 * - Plain text (including Jinja2 template bodies with \n\n paragraphs):
 *   each double-newline block becomes a <p>, single \n becomes <br>.
 * - Empty input: returns empty string (Tiptap shows placeholder).
 */
export function ensureHtml(content: string | null | undefined): string {
  if (!content) return "";
  const trimmed = content.trim();
  if (!trimmed) return "";

  // Already HTML: starts with an opening tag and has at least one closing tag.
  if (/^<[a-zA-Z]/.test(trimmed) && /<\/[a-zA-Z]/.test(trimmed)) {
    return content;
  }

  // Plain text / Jinja2 template → HTML paragraphs.
  return trimmed
    .split(/\n{2,}/)
    .filter(Boolean)
    .map((block) => `<p>${block.trim().replace(/\n/g, "<br>")}</p>`)
    .join("");
}

/**
 * Strips all HTML tags and decodes common entities.
 * Used for card preview text and word-count calculations.
 */
export function stripHtml(html: string | null | undefined): string {
  if (!html) return "";
  return html
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<\/p>/gi, " ")
    .replace(/<[^>]+>/g, "")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&nbsp;/g, " ")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s{2,}/g, " ")
    .trim();
}

/**
 * Returns true when the body string is HTML output from the Tiptap RTE,
 * as opposed to legacy plain text.
 *
 * Used by the backend scheduler patch to route body correctly through
 * body_html vs body_text MailBridge fields.
 */
export function isHtmlBody(body: string | null | undefined): boolean {
  if (!body) return false;
  const s = body.trimStart();
  return (
    s.startsWith("<") &&
    (s.includes("</p>") ||
      s.includes("</h") ||
      s.includes("<br") ||
      s.includes("</ul>") ||
      s.includes("</ol>") ||
      s.includes("</li>") ||
      s.includes("</strong>") ||
      s.includes("</em>"))
  );
}

/**
 * Backend HTML-body detection — mirrors `is_html_body` inside
 * `_append_footer` in app/features/sequences/router.py (template-send).
 * Kept separate from isHtmlBody() because the marker set differs slightly
 * (the backend does not treat </strong> or </em> alone as HTML).
 */
function isHtmlBodyForFooter(text: string): boolean {
  const s = text.trimStart();
  return (
    s.length > 0 &&
    s[0] === "<" &&
    ["</p>", "</h", "<br", "</ul>", "</ol>", "</li>"].some((m) => text.includes(m))
  );
}

/**
 * Appends the sender signature and the CAN-SPAM unsubscribe footer exactly
 * the way the backend does when a template is rendered for prospects
 * (POST /api/v1/sequences/template-send → `_append_footer`).
 *
 * Rules (same as backend):
 * - Signature is appended when present AND not already inside the body.
 * - Unsubscribe footer is appended only when the body does not already
 *   contain `{{unsubscribe_url}}` / `{{ unsubscribe_url }}`.
 * - Physical address follows the unsubscribe line when present.
 * - HTML bodies get <p> blocks; plain-text bodies get "\n\n"-separated blocks.
 *
 * Used by the pre-render template preview on the Sequences page so the
 * preview matches what "Render for All Prospects" produces.
 */
export function appendEmailFooter(
  bodyText: string | null | undefined,
  emailSignature: string | null | undefined,
  physicalAddress: string | null | undefined,
): string {
  const text = bodyText ?? "";
  const sig = (emailSignature ?? "").trim();
  const addr = (physicalAddress ?? "").trim();
  const isHtml = isHtmlBodyForFooter(text);
  const parts: string[] = [];

  if (sig && !text.includes(sig)) {
    parts.push(isHtml ? `<p>${sig.split(/\r\n|\r|\n/).join("<br>")}</p>` : sig);
  }

  const hasUnsubPlaceholder =
    text.includes("{{unsubscribe_url}}") || text.includes("{{ unsubscribe_url }}");
  if (!hasUnsubPlaceholder) {
    const unsubLine = "To unsubscribe: {{unsubscribe_url}}";
    if (isHtml) {
      parts.push(addr ? `<p>---<br>${unsubLine}<br>${addr}</p>` : `<p>---<br>${unsubLine}</p>`);
    } else {
      parts.push(addr ? `---\n${unsubLine}\n${addr}` : `---\n${unsubLine}`);
    }
  }

  if (parts.length === 0) return text;
  return isHtml
    ? text.trimEnd() + parts.join("")
    : text.trimEnd() + "\n\n" + parts.join("\n\n");
}

/**
 * Wraps Jinja2 `{{ variables }}` in a highlight span so previews show which
 * parts are filled in per prospect. Only text between tags is touched —
 * attributes such as href="{{unsubscribe_url}}" are left unchanged.
 */
export function highlightTemplateVariables(html: string | null | undefined): string {
  if (!html) return "";
  return html
    .split(/(<[^>]*>)/g)
    .map((part) =>
      part.startsWith("<")
        ? part
        : part.replace(/\{\{\s*\w+\s*\}\}/g, (m) => `<span class="outrena-tpl-var">${m}</span>`),
    )
    .join("");
}

/**
 * Builds the HTML shown by <EmailBodyPreview>: optional backend-identical
 * signature/footer, plain text converted to paragraphs, variables highlighted.
 */
export function buildEmailPreviewHtml({
  bodyHtml,
  emailSignature,
  physicalAddress,
  showFooter = false,
  highlightVariables = true,
}: {
  bodyHtml: string | null | undefined;
  emailSignature?: string | null;
  physicalAddress?: string | null;
  showFooter?: boolean;
  highlightVariables?: boolean;
}): string {
  const body = showFooter
    ? appendEmailFooter(bodyHtml, emailSignature, physicalAddress)
    : (bodyHtml ?? "");
  const html = ensureHtml(body);
  return highlightVariables ? highlightTemplateVariables(html) : html;
}